import { ArrowLeftRight, ChevronDown, Languages, Lock, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * TranslateScreen — Nachbau der hauseigenen Übersetzen-Ansicht aus dem
 * bunker.ai Produkt. Rendert ausschließlich den Hauptinhalt; die App-Shell
 * (Sidebar, Header) liefert die übergeordnete Demo-Hülle. Styling ist unter
 * `.bnk-translate` gescoped, Farben sind die Produkt-Tokens (kein Marketing-Theme).
 */

const T = {
  appBg: 'var(--bnkp-appbg, #0D1117)',
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  secondary: 'var(--bnkp-recessed, #0A0F1C)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  text: 'var(--bnkp-text, #E2E8F0)',
  textSec: 'var(--bnkp-text2, #94A3C2)',
  textTer: 'var(--bnkp-text3, #4A5568)',
  primary: 'var(--bnkp-primary, #2D7EFF)',
  green: 'var(--bnkp-green, #34D399)',
} as const;

function LangSelect({ label, id }: { label: string; id: string }) {
  return (
    <button type="button" id={id} className="bnk-tr-lang">
      <Languages size={15} strokeWidth={2.25} aria-hidden="true" />
      <span>{label}</span>
      <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" />
    </button>
  );
}

export function TranslateScreen({ className }: { className?: string }) {
  const { t } = useTranslation('demo');
  return (
    <div className={`bnk-translate${className ? ` ${className}` : ''}`}>
      <style>{css}</style>

      <header className="bnk-tr-head">
        <div className="bnk-tr-titlewrap">
          <span className="bnk-tr-iconpill" aria-hidden="true">
            <Languages size={20} strokeWidth={2.25} />
          </span>
          <div>
            <h1 className="bnk-tr-title">{t('translate.title')}</h1>
            <p className="bnk-tr-sub">{t('translate.subtitle')}</p>
          </div>
        </div>
        <span className="bnk-tr-badge">
          <span className="bnk-tr-dot" aria-hidden="true" />
          {t('translate.badge')}
        </span>
      </header>

      <div className="bnk-tr-grid">
        <section className="bnk-tr-pane" aria-label={t('translate.sourcePaneLabel')}>
          <div className="bnk-tr-pane-head">
            <LangSelect id="bnk-tr-src" label={t('translate.sourceLang')} />
            <span className="bnk-tr-count">{t('translate.charCount')}</span>
          </div>
          <textarea
            className="bnk-tr-area"
            aria-labelledby="bnk-tr-src"
            defaultValue={t('translate.sourceText')}
            spellCheck={false}
          />
        </section>

        <div className="bnk-tr-swapwrap">
          <button type="button" className="bnk-tr-swap" aria-label={t('translate.swapLabel')}>
            <ArrowLeftRight size={18} strokeWidth={2.5} aria-hidden="true" />
          </button>
        </div>

        <section className="bnk-tr-pane bnk-tr-pane--target" aria-label={t('translate.targetPaneLabel')}>
          <div className="bnk-tr-pane-head">
            <LangSelect id="bnk-tr-tgt" label={t('translate.targetLang')} />
            <span className="bnk-tr-count bnk-tr-count--ok">{t('translate.statusDone')}</span>
          </div>
          <textarea
            className="bnk-tr-area bnk-tr-area--target"
            aria-labelledby="bnk-tr-tgt"
            defaultValue={t('translate.targetText')}
            spellCheck={false}
            readOnly
          />
        </section>
      </div>

      <div className="bnk-tr-footer">
        <button type="button" className="bnk-tr-go">
          <Sparkles size={17} strokeWidth={2.25} aria-hidden="true" />
          {t('translate.button')}
        </button>
        <p className="bnk-tr-caption">
          <Lock size={13} strokeWidth={2.25} aria-hidden="true" />
          {t('translate.caption')}
        </p>
      </div>
    </div>
  );
}

const css = `
.bnk-translate {
  --tr-bg: ${T.appBg}; --tr-surface: ${T.surface}; --tr-elev: ${T.elevated};
  --tr-sec: ${T.secondary}; --tr-border: ${T.border}; --tr-border2: ${T.borderStrong};
  --tr-text: ${T.text}; --tr-sec-text: ${T.textSec}; --tr-ter: ${T.textTer};
  --tr-primary: ${T.primary}; --tr-green: ${T.green};
  font-family: Inter, system-ui, sans-serif;
  color: var(--tr-text);
  width: 100%; max-width: 1040px; margin: 0 auto;
  padding: clamp(20px, 3vw, 32px);
  box-sizing: border-box;
}
.bnk-translate *, .bnk-translate *::before, .bnk-translate *::after { box-sizing: border-box; }

.bnk-tr-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 22px;
}
.bnk-tr-titlewrap { display: flex; align-items: center; gap: 14px; }
.bnk-tr-iconpill {
  display: grid; place-items: center; width: 42px; height: 42px; flex: none;
  border-radius: 12px; color: var(--tr-primary);
  background: rgba(45, 126, 255, 0.1);
  border: 1px solid rgba(45, 126, 255, 0.22);
}
.bnk-tr-title { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15; }
.bnk-tr-sub { margin: 3px 0 0; font-size: 13.5px; color: var(--tr-sec-text); }

.bnk-tr-badge {
  display: inline-flex; align-items: center; gap: 7px; flex: none;
  padding: 7px 13px; border-radius: 999px; font-size: 12.5px; font-weight: 600;
  color: var(--tr-green);
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.25);
}
.bnk-tr-dot {
  width: 7px; height: 7px; border-radius: 50%; background: var(--tr-green);
  box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6);
  animation: bnk-tr-pulse 2.4s ease-out infinite;
}
@keyframes bnk-tr-pulse {
  0% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55); }
  70% { box-shadow: 0 0 0 7px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
}

.bnk-tr-grid {
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: stretch; gap: 0;
}
.bnk-tr-pane {
  background: var(--tr-surface); border: 1px solid var(--tr-border);
  border-radius: 14px; padding: 14px; display: flex; flex-direction: column;
  min-height: 280px; box-shadow: 0 8px 26px rgba(0, 0, 0, 0.28);
}
.bnk-tr-pane--target { background: var(--tr-sec); }
.bnk-tr-pane-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }

.bnk-tr-lang {
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  padding: 7px 12px; border-radius: 9px; font: inherit; font-size: 13.5px; font-weight: 600;
  color: var(--tr-text); background: var(--tr-elev);
  border: 1px solid var(--tr-border2); transition: border-color .15s, background .15s;
}
.bnk-tr-lang:hover { border-color: var(--tr-primary); background: rgba(255, 255, 255, 0.04); }
.bnk-tr-lang svg:first-child { color: var(--tr-primary); }
.bnk-tr-lang svg:last-child { color: var(--tr-sec-text); }

.bnk-tr-count { font-size: 12px; color: var(--tr-ter); font-variant-numeric: tabular-nums; }
.bnk-tr-count--ok { color: var(--tr-green); font-weight: 600; }

.bnk-tr-area {
  flex: 1; width: 100%; resize: none; border: 0; outline: none; background: transparent;
  font: inherit; font-size: 14px; line-height: 1.65; color: var(--tr-text);
  white-space: pre-wrap; padding: 2px;
}
.bnk-tr-area::placeholder { color: var(--tr-ter); }
.bnk-tr-area--target { color: var(--tr-sec-text); }

.bnk-tr-swapwrap { display: grid; place-items: center; padding: 0 14px; }
.bnk-tr-swap {
  display: grid; place-items: center; width: 40px; height: 40px; cursor: pointer;
  border-radius: 50%; color: var(--tr-sec-text);
  background: var(--tr-elev); border: 1px solid var(--tr-border2);
  transition: transform .2s, color .15s, border-color .15s, background .15s;
}
.bnk-tr-swap:hover {
  color: var(--tr-primary); border-color: var(--tr-primary);
  background: rgba(45, 126, 255, 0.1); transform: rotate(180deg);
}

.bnk-tr-footer {
  margin-top: 20px; display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
}
.bnk-tr-go {
  display: inline-flex; align-items: center; gap: 9px; cursor: pointer;
  padding: 11px 22px; border-radius: 11px; border: 0; font: inherit;
  font-size: 14.5px; font-weight: 700; color: #fff;
  background: linear-gradient(180deg, ${T.primary} 0%, #1F66E0 100%);
  box-shadow: 0 6px 18px rgba(45, 126, 255, 0.32); transition: filter .15s, transform .1s;
}
.bnk-tr-go:hover { filter: brightness(1.08); }
.bnk-tr-go:active { transform: translateY(1px); }
.bnk-tr-caption { margin: 0; display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--tr-sec-text); }
.bnk-tr-caption svg { color: var(--tr-ter); }

@media (max-width: 680px) {
  .bnk-tr-grid { grid-template-columns: 1fr; gap: 12px; }
  .bnk-tr-swapwrap { padding: 0; }
  .bnk-tr-swap:hover { transform: rotate(90deg); }
}
`;
